import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SeanceGraph from "./SeanceGraph";
import SeanceTable from "./SeanceTable";

interface Seance {
  sujet: string;
  fiable: boolean;
  sessionDate: string;
  // @ts-expect-error // Ignore TS error for dynamic properties
  parameters: { content: string }[]; // Fixed field for parameters
  resultsOB?: string | null;
  resultsOT?: string | null;
  resultsPVS?: string | null;
  resultsZPCCF?: string | null;
  resultsZPFCF?: string | null;
  resultsZPI?: string | null;
  resultsZPL?: string | null;
  order: number;
  accountUsed: "PATIENT" | "CLINIQUE";
  screenUsed: string;

  [key: string]: string | number | boolean | null | undefined; // Allow other dynamic properties

}

const SeancesDashboard = () => {
  const [seances, setSeances] = useState<Seance[]>([]);
  const [filteredSeances, setFilteredSeances] = useState<Seance[]>([]);
  const [selectedExercise, setSelectedExercise] = useState(""); // State for selected exercise
  const { patientId } = useParams<{ patientId: string }>();

  // Fetching the session data from the backend

  useEffect(() => {
    const getResults = () => {
      axios
        .get(`/practitioner/sessionswithparams/${patientId}`)
        .then((response) => {
          // Automatically filter for seances with fiable set to true
          const initialFilteredSeances = response.data.filter(
            (seance: Seance) => seance.fiable
          );
          setSeances(initialFilteredSeances); // Initialize with seances where fiable is true
        });
    };
    getResults();
  }, [patientId]);

  // Filter function to filter the seances based on the selected exercise and fiable status
  const filterResult = (exerciseType: string) => {
    setSelectedExercise(exerciseType); // Set the selected exercise
    const filtered = seances.filter((seance: Seance) => {
      // Only retain sessions where fiable is true and the selected exercise is not null or empty
      return (
        seance.fiable &&
        seance[exerciseType] !== null &&
        seance[exerciseType] !== "" &&
        seance[exerciseType] !== "0"
      );
    });
    setFilteredSeances(filtered);
  };

  return (
    <Card className="w-5/6 min-h-5/6 pt-6" id="seances-dashboard-card">
      <CardHeader className="border-b mb-6 flex flex-col">
        <CardTitle className="text-center mb-4">
          Résultats des séances de rééducation
        </CardTitle>
        <div className="flex flex-wrap gap-2 justify-center">
          {/* Champ Visuel - Couleur bleu */}
          <Button
            onClick={() => filterResult("resultsZPL")}
            className={`bg-module-champ-row hover:bg-module-champ-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsZPL"
                ? "bg-module-champ-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Champ Profond Localisation
          </Button>
          <Button
            onClick={() => filterResult("resultsZPI")}
            className={`bg-module-champ-row hover:bg-module-champ-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsZPI"
                ? "bg-module-champ-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Champ Profond Identification
          </Button>
          <Button
            onClick={() => filterResult("resultsZPCCF")}
            className={`bg-module-champ-row hover:bg-module-champ-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsZPCCF"
                ? "bg-module-champ-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Champ Profond Couleurs
          </Button>
          <Button
            onClick={() => filterResult("resultsZPFCF")}
            className={`bg-module-champ-row hover:bg-module-champ-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsZPFCF"
                ? "bg-module-champ-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Champ Profond Formes
          </Button>

          {/* Poursuite Visuelle */}
          <Button
            onClick={() => filterResult("resultsPVS")}
            className={`bg-module-poursuite-row hover:bg-module-poursuite-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsPVS"
                ? "bg-module-poursuite-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Poursuite Visuelle Smooth
          </Button>

          {/* Optocinétique */}
          <Button
            onClick={() => filterResult("resultsOT")}
            className={`bg-module-optocinetique-row hover:bg-module-optocinetique-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsOT"
                ? "bg-module-optocinetique-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Lecture Texte
          </Button>
          <Button
            onClick={() => filterResult("resultsOB")}
            className={`bg-module-optocinetique-row hover:bg-module-optocinetique-header text-black hover:text-white border border-gray-300 ${
              selectedExercise === "resultsOB"
                ? "bg-module-optocinetique-header text-white"
                : ""
            }`}
            variant="outline"
          >
            Lecture Barres
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Graph */}
        <div id="seances-graph" className="w-11/12 flex justify-center pl-14 pr-7">
          <SeanceGraph
            seances={filteredSeances}
            selectedExercise={selectedExercise}
          />
        </div>

        {/* Table */}
        <div id="seances-table" className="w-11/12 flex mt-4 ml-2 pl-7 pr-7 ">
          <SeanceTable
            seances={filteredSeances}
            selectedExercise={selectedExercise}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-center"></CardFooter>
    </Card>
  );
};

export default SeancesDashboard;
